import React, { useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, Redirect } from "react-router-dom";
import { Loader, Grid, Form, Button, Container, Message } from "semantic-ui-react";
import { ControlledInput } from "../ControlledForm";
import { forgotPassword } from "../../actions/identity";
import identitySelectors from "../../selectors/identity";
import clientsSelectors from "../../selectors/clients";
import { getClient } from "../../actions/clients";
import queryString from "query-string";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/";
import * as yup from "yup";
import _ from "lodash";

export const ForgotPasswordForm = ({ isPublic }: { isPublic: boolean }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { site } = useParams<{ site: string }>();
  const query = useMemo(() => queryString.parse(window.location.search), [window.location.search]);
  const { redirect } = query;
  const postForgotPassword = useSelector(identitySelectors.postForgotPassword);
  const getClientSelector = useSelector(clientsSelectors.getClient);
  const authenticationType = _.get(getClientSelector, "payload.data.attributes.authentication-type");

  const schema = yup.object().shape({
    email: yup.string().email().required(),
  });

  const { errors, control, watch, handleSubmit } = useForm({
    mode: "onBlur",
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(schema),
  });

  const watchEmail = watch("email");

  const onSubmit = function () {
    if (_.isEmpty(watchEmail)) return;
    dispatch(forgotPassword({ site, email: watchEmail, redirect: redirect as string }));
  };

  const forgotPasswordFailure = postForgotPassword.error;
  useEffect(() => {
    if (!getClientSelector.payload) dispatch(getClient(site));
  }, [dispatch, getClientSelector.payload, site]);

  if (postForgotPassword.payload) {
    if (redirect) {
      return (
        <Redirect
          to={`/${site}${isPublic ? "/public" : ""}/login?email=${watchEmail}&redirect=${encodeURIComponent(
            redirect as string,
          )}`}
        />
      );
    }
    return <Redirect to={`/${site}${isPublic ? "/public" : ""}/login?email=${watchEmail}`} />;
  }
  if (!authenticationType) {
    return <Loader active inline='centered' />;
  }

  if (authenticationType === "ldap" && !isPublic) {
    return (
      <>
        <Form>
          <Container className='Message'>
            <p>Your agency is configured to authenticate against your network.</p>
            <p>
              <b>Please use your Windows username and password.</b>
            </p>
            <p>If you cannot remember your login, you will need to contact your network administrator.</p>
          </Container>
          <Form.Field>
            <Grid>
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Button primary basic fluid size='large' onClick={() => history.goBack()}>
                    Back
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form.Field>
        </Form>
      </>
    );
  }

  return (
    <>
      {forgotPasswordFailure && (
        <Message negative className='feedback tw-mb-4' aria-live='polite'>
          {_.get(postForgotPassword, "error.response.message", "This email address was not found in our system")}
        </Message>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Container className='tw-mb-4 tw-whitespace-pre-line'>
          {`
            Don't worry, everyone forgets their password.\n
            Just enter your email below and we will send you a link to reset your password.
          `}
        </Container>

        <Form.Field required error={errors.email}>
          <ControlledInput name='email' control={control} label='Email' />
        </Form.Field>

        <Button
          className='tw-mr-4 tw-px-6 tw-py-3 tw-text-lg tw-leading-[1.125]'
          type='submit'
          primary
          size='medium'
          loading={postForgotPassword.loading}
          disabled={postForgotPassword.loading}
        >
          Continue
        </Button>
        <Button
          className='tw-px-6 tw-py-3 tw-text-lg tw-leading-[1.125]'
          type='button'
          size='medium'
          onClick={() =>
            history.push(
              `/${site}/public/login${redirect ? `?redirect=${encodeURIComponent(redirect as string)}` : ""}`,
            )
          }
        >
          Cancel
        </Button>
      </Form>
    </>
  );
};

export default ForgotPasswordForm;
